import assetUtils, { Asset, getItokenAddressInfo } from 'app-lib/assetUtils'
import _axios from 'axios'
import { chainConfigs, ChainId } from 'bzx-common/src/config/appConfig'
import { BigNumber } from '@0x/utils'
import { TreasuryBalance } from 'bzx-common/src/stores/StatsStore/StatsStore'

const BZX_API_URL = process.env.BZX_API_URL

const axios = _axios.create({
  baseURL: BZX_API_URL,
  timeout: 15000,
})

/**
 * Try to get the original error message from server.
 * @param {Error} error Error from a request
 */
function normalizeRequestError(error: any) {
  if (error && error.response && error.response.data) {
    const errorData = error.response.data
    if (errorData.message) {
      error.message = errorData.message
    }
    if (errorData.errorCode) {
      error.code = errorData.errorCode
    }
  }
  return error
}

export interface IInterestRates {
  lendApr: number
  lendApy: number
  borrowApr: number
  borrowApy: number
  yieldFarmingAPR: number
}

export interface IAssetsStatsHistory {
  token: Asset
  timestamp: string
  tvlUsd: number
  tvlUsdChange: number
  tvl?: number
  tvlChange?: number
  apr?: number
  aprChange?: number
}

export type ApiNetworkName = 'eth' | 'bsc' | 'polygon' | 'arb' | 'optimism'

/** This is the current mapping we have in the web api; should replace by chainId on api side. */
export const apiNetworkNames: Record<ChainId, ApiNetworkName> = {
  1: 'eth',
  10: 'optimism',
  56: 'bsc',
  137: 'polygon',
  42161: 'arb',
}

export const apiNetworkToChainId: Record<ApiNetworkName, ChainId> = {
  eth: 1,
  optimism: 10,
  bsc: 56,
  polygon: 137,
  arb: 42161,
}

export interface IInterestRatesFromApi {
  lendApr: number
  lendApy: number
  borrowApr: number
  borrowApy: number
  yieldFarmingAPR: number
}

export const getTreasuryBalances = async (): Promise<TreasuryBalance[]> => {
  const response = await axios.get<{ data: { asset: Asset; amount: string; amountUSD: string }[] }>(
    '/treasury/balances'
  )
  return response.data.data.map((a) => ({
    // we have decided to show vbzrx as is, like in staking, so we map vooki to vbzrx
    asset: a.asset === Asset.vOOKI ? Asset.vBZRX : a.asset,
    amount: new BigNumber(a.amount),
    amountUSD: new BigNumber(a.amountUSD),
  }))
}

export type LoanParamsData = {
  loanId: string // "0xac6653ed4f664f3e17e9a2a945bf9551102539ab392e4a23b410843d5356aa45",
  principal: Asset //"CAKE",
  collateral: Asset //"AUTO",
  platform: string // "Fulcrum",
  initialMargin: number // 20,
  maintenanceMargin: number // 15,
  liquidationPenalty: number // 7
}

const getLoanParams = async (
  chainId: ChainId,
  platform: string,
  loanAsset: Asset,
  collateralAsset: Asset
): Promise<LoanParamsData | undefined> => {
  const principal = assetUtils.wrapNativeAsset(chainId, loanAsset)
  const collateral = assetUtils.wrapNativeAsset(chainId, collateralAsset)
  const networks = chainId
  const type = platform.toLowerCase() === 'fulcrum' ? 'trade' : 'borrow'
  const loanParams = await fetch(
    `${BZX_API_URL}/loan-params?networks=${networks}&principal=${principal}&collateral=${collateral}&type=${type}`
  )
  const data: LoanParamsData[] = (await loanParams.json()).data[apiNetworkNames[chainId]]
  const loanParam = data.find(
    (param) =>
      param.platform.toLowerCase() === platform.toLowerCase() &&
      param.principal === principal &&
      param.collateral === collateral
  )
  return loanParam
}

const subgraphFetch = async (
  query: string,
  chainId: ChainId,
  opts?: { oldGraph: boolean }
): Promise<any> => {
  // We have graph for old contracts. Some users wanted their old trade history
  const network = apiNetworkNames[chainId] + (opts?.oldGraph ? '_old' : '')
  const res = await fetch(`${process.env.BZX_API_URL}/graph/stats/${network}/proxy`, {
    method: 'post',
    body: JSON.stringify({ query }),
    headers: { 'Content-Type': 'application/json' },
  })
  const data = await res.json()
  if (data.success === false) {
    throw new Error(`Failed ${BZX_API_URL}/graph/stats/${network}/proxy`)
  }
  return data
}

export interface IApiAirdrop {
  airdropIndex: number
  amount: BigNumber
  index: BigNumber
  proof: string[]
  token: {
    symbol: string
    address: string
    decimals: number
  }
}

/**
 * https://api.ooki.com/v1/airdrops?index=0&networks=eth&address=0xad65de4d66ba3381f4C244c238aed5012cd53d3b
 * Note: the API is fu**, sometimes sent "empty response" or 0 amounts if the user has no airdrop
 * until it's fixed, need to check if the data is actually there.
 */
const getUserAirdropInfo = async (
  chainId: ChainId,
  airdropIndex: number,
  address: string
): Promise<IApiAirdrop> => {
  try {
    const network = apiNetworkNames[chainId]
    const params = {
      networks: network,
      index: airdropIndex,
      address,
    }
    const { data } = await axios.get('/airdrops', { params })
    const info = data.data[network][airdropIndex]
    return {
      airdropIndex,
      amount: new BigNumber(info && info.amount ? info.amount : 0),
      index: new BigNumber(info && typeof info.index === 'number' ? info.index : -1), // set to -1 to indicate the user has no airdrop
      proof: info && info.proof ? info.proof : [],
      token: {
        symbol: info.token.name.toUpperCase(), // API is sending "name" instead of "symbol"
        address: info.token.erc20Address,
        decimals: info.token.decimals,
      },
    }
  } catch (error) {
    throw normalizeRequestError(error)
  }
}

/**
 * This is the airdrop where users have to send locked bzrx to our contract on BSC and get it back on Polygon
 */
async function getBzrxLockedAirdrops(address: string): Promise<IApiAirdrop[]> {
  try {
    const params = {
      address,
    }
    const { data } = await axios.get('/airdrops/locked-bzrx', { params })
    data.data.forEach((airdrop: any) => {
      airdrop.token.symbol = airdrop.token.name.toUpperCase()
      airdrop.token.address = airdrop.token.erc20Address
      airdrop.amount = new BigNumber(airdrop.amount || 0).div(10 ** airdrop.token.decimals)
      airdrop.index = new BigNumber(airdrop.index)
      delete airdrop.token.name
      delete airdrop.token.erc20Address
    })
    return data.data
  } catch (error) {
    throw normalizeRequestError(error)
  }
}

export interface IChainTokenStat {
  address: string
  availableLiquidity: string
  borrowApr: string
  supplyApr: string
  tokenName: Asset
  tokenUsdPrice: string
  totalBorrow: string
  totalSupply: string
  usdTotalLocked: string
  usdSupply: string
  vaultBalance: string
  decimals: number
  iTokenAddress: string
  iTokenExplorerUrl: string
  logo: string
}

export interface IAllTokensStats {
  usdSupply: string
  usdTotalLocked: string
}

export interface IChainStat {
  chainId: ChainId
  stats: IChainTokenStat[]
  allTokensStats: IAllTokensStats
}

/**
 * Returns an array of chainId with their corresponding tokens reserve and rates
 * chainId can be passed optionally to get stats for a single chain
 *
 * `[{chainId: 1, stats: IChainTokenStat[], allTokensStats:{}}, {chainId: 137, stats: ...}, ...]`
 */
async function getChainStats(chainId?: ChainId): Promise<IChainStat[]> {
  const url = chainId ? `/stats/chain/${chainId}` : `/stats`
  try {
    const { data } = await axios.get<{ data: IChainStat[] | IChainStat }>(url)
    const stats = Array.isArray(data.data) ? data.data : [data.data]
    stats.forEach((stat) => {
      const chain = chainConfigs[stat.chainId]
      if (!chain) {
        return
      }
      stat.chainId = chain.chainDetails.chainId
      stat.stats.forEach((token) => {
        if (chain.native.asset === token.tokenName) {
          // We want to use the wrapped asset everywhere
          token.tokenName = chain.native.wrapped
        }
        if (token.tokenName === 'ETH' && chainId === 137) {
          // fixes a problem with polygon, the api sends ETH instead of WETH
          token.tokenName = Asset.WETH
        }
        const { decimals, iTokenAddress, iTokenExplorerUrl } = getItokenAddressInfo(
          stat.chainId,
          token.tokenName
        )
        const assetsCommon = assetUtils.getCommon(token.tokenName)
        token.iTokenAddress = iTokenAddress
        token.iTokenExplorerUrl = iTokenExplorerUrl
        token.decimals = decimals
        token.logo = assetsCommon ? assetsCommon.spriteLogo : ''
      })
    })
    // always return an array for a consistent response
    return stats
  } catch (err) {
    throw normalizeRequestError(err)
  }
}

export interface IUserYieldApi {
  iTokenAddress: string
  iTokenPrice: string // price in asset
  userItokenPrice: string
  usdPrice: number
}

async function getUserItokenPrice(params: {
  chainId: ChainId
  address: string
}): Promise<IUserYieldApi[]> {
  try {
    const { data } = await axios.get<IUserYieldApi[]>('/user-itoken-price', { params })
    return data
  } catch (err) {
    throw normalizeRequestError(err)
  }
}

async function postFingerPrint(info: { fp: any; wallet: string }) {
  return axios.post('/fpr', info)
}

/**
 * Returns an array with history prepared for chart
 * asset can be passed optionally to get assets stats history or tvl history data
 */
const getAssetStatsHistory = async (chainId: ChainId, assetStats = false, pointsNumber = 30) => {
  try {
    const startData = new Date().setDate(new Date().getDate() - pointsNumber)
    const endData = new Date().getTime()
    const requestUrl = assetStats
      ? `${BZX_API_URL}/asset-stats-history?start_date=${startData}&end_date=${endData}&points_number=${pointsNumber}&chainId=${chainId}`
      : `${BZX_API_URL}/tvl-history?start_date=${startData}&end_date=${endData}&points_number=${pointsNumber}&chainId=${chainId}`
    const response = await fetch(requestUrl)
    const responseJson = await response.json()
    return responseJson.data
  } catch (err) {
    throw normalizeRequestError(err)
  }
}

export default {
  getBzrxLockedAirdrops,
  getChainStats,
  getLoanParams,
  getUserAirdropInfo,
  getUserItokenPrice,
  postFingerPrint,
  subgraphFetch,
  getAssetStatsHistory,
}
